import { useAutoAnimate } from "@formkit/auto-animate/react";
import { XMarkIcon } from "@heroicons/react/20/solid";
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import { useNotifications } from "@via/hooks/useNotifications";
import { type JSX, Fragment } from "react";

export default function Notifications(): JSX.Element {
  const { notifications, clearNotification, NOTIFICATION } = useNotifications();
  const [parent] = useAutoAnimate({ duration: 350 });

  const renderNotificationIcon = (
    variant: typeof NOTIFICATION
  ): JSX.Element => {
    switch (variant) {
      case NOTIFICATION.SUCCESS:
        return (
          <CheckCircleIcon
            className="h-6 w-6 text-green-400"
            aria-hidden="true"
          />
        );
      case NOTIFICATION.ERROR:
        return (
          <ExclamationCircleIcon
            className="h-6 w-6 text-red-400"
            aria-hidden="true"
          />
        );
      case NOTIFICATION.ALERT:
        return (
          <ExclamationTriangleIcon
            className="h-6 w-6 text-yellow-400"
            aria-hidden="true"
          />
        );
      default:
        return <Fragment />;
    }
  };

  return (
    <>
      <div
        id="notification"
        ref={parent}
        aria-live="assertive"
        className="pointer-events-none fixed inset-0 z-[500] flex flex-col justify-end px-4 py-6 sm:items-center sm:p-6">
        {notifications.map(({ id, message, details, variant }) => (
          <div
            key={id}
            className="pointer-events-auto relative w-full max-w-sm overflow-hidden rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5">
            <div className="p-4">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  {renderNotificationIcon(variant)}
                </div>
                <div className="ml-3 w-0 flex-1">
                  <p className="text-sm font-medium text-gray-900">{message}</p>
                  <p className="break-words mt-1 text-sm text-gray-500">
                    {details}
                  </p>
                </div>
                <div className="ml-4 flex flex-shrink-0">
                  <button
                    type="button"
                    className="inline-flex rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                    onClick={() => {
                      clearNotification(id);
                    }}>
                    <span className="sr-only">Close</span>
                    <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}
